/**
 * Dimension Tool — Annotate > Dimension panel.
 *
 * Implements the six BIM dimension kinds behind one parameterized tool:
 *   aligned   (DI)  — distance between two references, parallel to them
 *   linear    — horizontal / vertical (X or Y) distance only
 *   angular   — angle between two 2-point lines
 *   radial    — radius of a circle/arc (center + rim)
 *   diameter  — diameter of a circle/arc (center + rim)
 *   arclength — length of an arc (center + start + end)
 *
 * The tool is placed on a 2D drafting plane (Z-up world): references are
 * picked with vertex/grid snapping, then a third click drops the dimension
 * (or witness) line. Aligned/linear chain like BIM: after placement the
 * second reference becomes the next first reference.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export type DimensionKind =
    | 'aligned' | 'linear' | 'angular'
    | 'radial' | 'diameter' | 'arclength';

export interface DimensionGeometryData {
    kind: DimensionKind;
    /** aligned/linear: the two reference points. */
    refs: [number, number, number][];
    /** aligned/linear: where the dimension line was dropped. */
    dimStart?: [number, number, number];
    dimEnd?: [number, number, number];
    /** angular: two lines (4 points) + text position. */
    line1?: [number, number, number][];
    line2?: [number, number, number][];
    /** radial/diameter/arclength: circle definition. */
    center?: [number, number, number];
    rim?: [number, number, number];
    /** arclength extra points. */
    arcStart?: [number, number, number];
    arcEnd?: [number, number, number];
    /** Where the dimension text is anchored (all kinds). */
    textPos?: [number, number, number];
    /** Options Bar text placement for linear/aligned dimensions. */
    textPosition?: 'Above' | 'Below' | 'Center';
    /** Element ids referenced by witness lines (optional). */
    witnessRefs?: string[];
}

export interface DimensionElement extends Element {
    category: 'Dimensions';
    geometryData: DimensionGeometryData;
}

const KIND_NAMES: Record<DimensionKind, string> = {
    aligned: 'Aligned Dimension',
    linear: 'Linear Dimension',
    angular: 'Angular Dimension',
    radial: 'Radial Dimension',
    diameter: 'Diameter Dimension',
    arclength: 'Arc Length Dimension',
};

/** How many reference clicks the kind needs before the placement click. */
const STAGES: Record<DimensionKind, number> = {
    aligned: 2, linear: 2, angular: 4, radial: 2, diameter: 2, arclength: 3,
};

export class DimensionTool extends BaseTool {
    id = 'dimension';
    name: string;
    cursor = 'crosshair';

    private kind: DimensionKind;
    /** Staged reference points (vertex/grid snapped). */
    private refs: THREE.Vector3[] = [];
    /** Element id captured at each staged reference (null for grid snaps). */
    private witnessIds: (string | null)[] = [];
    /** Element id under the most recent snap (vertex hits only). */
    private lastSnapId: string | null = null;
    private current: THREE.Vector3 | null = null;

    constructor(kind: DimensionKind = 'aligned') {
        super();
        this.kind = kind;
        this.name = KIND_NAMES[kind];
        this.id = `dimension_${kind}`;
    }

    protected onActivate(): void {
        this.refs = [];
        this.witnessIds = [];
        this.current = null;
        const hint = {
            aligned: 'Aligned: pick the first reference, then the second, then drop the line.',
            linear: 'Linear: pick two points, then drop the horizontal/vertical line.',
            angular: 'Angular: pick 2 points on the first line, 2 on the second, then drop.',
            radial: 'Radial: pick the center, then a point on the rim, then drop the text.',
            diameter: 'Diameter: pick the center, then a point on the rim, then drop the text.',
            arclength: 'Arc length: pick center, arc start, arc end, then drop the text.',
        }[this.kind];
        this.notify(hint);
    }

    /** Vertex snap first (element corners), then grid — like BIM snapping.
     *  Also records the BIM element id behind a vertex snap so the placed
     *  dimension can carry witness references (renderer dashed ties). */
    private snapPoint(event: ThreeEvent<PointerEvent>): THREE.Vector3 | null {
        const point = this.getIntersection(event);
        if (!point) return null;
        const hits = this.raycastSceneAtEvent(event);
        for (const hit of hits) {
            if (!hit.object.visible || hit.object.userData?.isEventPlane === true) continue;
            const vertex = this.snapToMeshVertex(point, hit);
            if (vertex) {
                this.lastSnapId = this.resolveElementIdFromHit(hit);
                return vertex;
            }
        }
        this.lastSnapId = null;
        return this.snapToGrid(point, 0.1);
    }

    /** Closest corner of the hit mesh within a snap radius. */
    private snapToMeshVertex(point: THREE.Vector3, hit: THREE.Intersection): THREE.Vector3 | null {
        const mesh = hit.object as THREE.Mesh;
        if (!(mesh instanceof THREE.Mesh) || !mesh.geometry) return null;
        const positionAttribute = mesh.geometry.getAttribute('position');
        if (!positionAttribute) return null;
        const local = mesh.worldToLocal(point.clone());
        let best: THREE.Vector3 | null = null;
        let minDist = 0.5; // generous vertex snap radius (m)
        for (let i = 0; i < positionAttribute.count; i++) {
            const v = new THREE.Vector3().fromBufferAttribute(positionAttribute, i);
            const d = v.distanceTo(local);
            if (d < minDist) {
                minDist = d;
                best = v;
            }
        }
        return best ? mesh.localToWorld(best.clone()) : null;
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const snapped = this.snapPoint(event);
        if (!snapped) return;

        if (this.refs.length < STAGES[this.kind]) {
            this.refs.push(snapped);
            this.witnessIds.push(this.lastSnapId);
        } else {
            // Placement click: create the dimension, then restart/chain.
            this.createDimension(snapped);
            if (this.kind === 'aligned' || this.kind === 'linear') {
                // BIM chaining: the last reference anchors the next dim.
                this.refs = [this.refs[this.refs.length - 1]];
                this.witnessIds = [this.witnessIds[this.witnessIds.length - 1] ?? null];
            } else {
                this.refs = [];
                this.witnessIds = [];
            }
        }
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const snapped = this.snapPoint(event);
        if (snapped) {
            this.current = snapped;
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.refs.length > 0) {
                this.refs = [];
                this.witnessIds = [];
                this.current = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
            return;
        }
        super.onKeyDown(event);
    }

    private updatePreview() {
        if (!this.context) return;
        if (this.refs.length === 0 && !this.current) {
            this.context.appStore.setToolPreview(null);
            return;
        }
        this.context.appStore.setToolPreview({
            type: 'dimension',
            kind: this.kind,
            refs: this.refs.slice(),
            current: this.current,
            value: this.previewValue(),
        });
    }

    /** Live readout shown next to the rubber-band during the sketch. */
    private previewValue(): string {
        if (this.refs.length === 0) return '';
        switch (this.kind) {
            case 'aligned': {
                if (this.refs.length < 2) return '';
                return fmt(this.refs[0].distanceTo(this.refs[1])) + ' m';
            }
            case 'linear': {
                if (this.refs.length < 2) return '';
                const d = this.refs[1].clone().sub(this.refs[0]);
                const v = Math.abs(d.x) >= Math.abs(d.y) ? d.x : d.y;
                return fmt(Math.abs(v)) + ' m';
            }
            case 'angular': {
                if (this.refs.length < 4) return '';
                return fmtDeg(angleBetweenLines(this.refs[0], this.refs[1], this.refs[2], this.refs[3])) + '°';
            }
            case 'radial': {
                if (this.refs.length < 2) return '';
                return 'R ' + fmt(this.refs[0].distanceTo(this.refs[1])) + ' m';
            }
            case 'diameter': {
                if (this.refs.length < 2) return '';
                return '⌀ ' + fmt(2 * this.refs[0].distanceTo(this.refs[1])) + ' m';
            }
            case 'arclength': {
                if (this.refs.length < 3) return '';
                return 'L ' + fmt(arcLength(this.refs[0], this.refs[1], this.refs[2])) + ' m';
            }
        }
    }

    private createDimension(place: THREE.Vector3): void {
        if (!this.context || this.refs.length < STAGES[this.kind]) return;

        const geom: DimensionGeometryData = { kind: this.kind, refs: this.refs.map(toTuple) };
        // Options Bar text placement is honored by the linear/aligned
        // renderer via textPosition (the renderer offsets the label around
        // the dim-line midpoint with it).
        geom.textPosition = this.context.bimStore.toolProperties.dimensionTextPosition ?? 'Above';
        // Bind the vertex-snapped references as witness elements so the
        // renderer can draw the dashed ties (Edit Witness Lines adds/removes
        // them afterwards).
        const witness = this.witnessIds.filter((id): id is string => id !== null);
        if (witness.length > 0) geom.witnessRefs = Array.from(new Set(witness));
        let value = '';

        switch (this.kind) {
            case 'aligned': {
                const [a, b] = this.refs;
                // The dropped line runs parallel to the references through
                // the drop point (BIM behavior).
                const [dimStart, dimEnd] = projectOntoParallel(a, b, place);
                geom.dimStart = toTuple(dimStart);
                geom.dimEnd = toTuple(dimEnd);
                value = fmt(a.distanceTo(b)) + ' m';
                break;
            }
            case 'linear': {
                const [a, b] = this.refs;
                const horiz = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y);
                const y = horiz ? place.y : place.x;
                geom.dimStart = [horiz ? a.x : y, horiz ? y : a.y, place.z];
                geom.dimEnd = [horiz ? b.x : y, horiz ? y : b.y, place.z];
                value = fmt(Math.abs(horiz ? b.x - a.x : b.y - a.y)) + ' m';
                break;
            }
            case 'angular': {
                const [p1, p2, p3, p4] = this.refs;
                geom.line1 = [toTuple(p1), toTuple(p2)];
                geom.line2 = [toTuple(p3), toTuple(p4)];
                const v = angleBetweenLines(p1, p2, p3, p4);
                geom.textPos = toTuple(place);
                value = fmtDeg(v) + '°';
                break;
            }
            case 'radial':
            case 'diameter': {
                const [c, r] = this.refs;
                geom.center = toTuple(c);
                geom.rim = toTuple(r);
                geom.textPos = toTuple(place);
                const rad = c.distanceTo(r);
                value = (this.kind === 'radial' ? 'R ' : '⌀ ') + fmt(this.kind === 'radial' ? rad : rad * 2) + ' m';
                break;
            }
            case 'arclength': {
                const [c, s, e] = this.refs;
                geom.center = toTuple(c);
                geom.arcStart = toTuple(s);
                geom.arcEnd = toTuple(e);
                geom.rim = toTuple(s);
                geom.textPos = toTuple(place);
                value = 'L ' + fmt(arcLength(c, s, e)) + ' m';
                break;
            }
        }

        const element: DimensionElement = {
            id: uuidv4(),
            category: 'Dimensions',
            type: KIND_NAMES[this.kind],
            parameters: {
                'Value': value,
                'Kind': KIND_NAMES[this.kind],
            },
            constraints: [],
            geometryData: geom,
        };
        this.context.bimStore.addElement(element);
    }
}

// ==================== geometry helpers ====================

function toTuple(v: THREE.Vector3): [number, number, number] {
    return [v.x, v.y, v.z];
}

function fmt(n: number): string {
    return (Math.round(n * 1000) / 1000).toFixed(3);
}

function fmtDeg(n: number): string {
    return (Math.round(n * 10) / 10).toFixed(1);
}

/**
 * Parallel dimension line through `place` for references a–b: the endpoints
 * are the reference points slid along the offset direction onto the line
 * that passes through the drop point.
 */
function projectOntoParallel(
    a: THREE.Vector3, b: THREE.Vector3, place: THREE.Vector3
): [THREE.Vector3, THREE.Vector3] {
    const mid = a.clone().lerp(b, 0.5);
    const perp = place.clone().sub(mid);
    if (perp.lengthSq() < 1e-9) {
        return [a.clone(), b.clone()];
    }
    return [a.clone().add(perp), b.clone().add(perp)];
}

/** Acute/obtuse angle (deg) between line p1-p2 and line p3-p4. */
export function angleBetweenLines(
    p1: THREE.Vector3, p2: THREE.Vector3, p3: THREE.Vector3, p4: THREE.Vector3
): number {
    const d1 = p2.clone().sub(p1);
    const d2 = p4.clone().sub(p3);
    const cos = THREE.MathUtils.clamp(d1.normalize().dot(d2.normalize()), -1, 1);
    let deg = THREE.MathUtils.radToDeg(Math.acos(Math.abs(cos)));
    // Report the angle closest to what the two rays actually open toward.
    if (cos < 0 && deg < 90) deg = 180 - deg;
    return deg;
}

/** Arc length given center, start and end points (CCW sweep ≤ 360°). */
export function arcLength(center: THREE.Vector3, start: THREE.Vector3, end: THREE.Vector3): number {
    const radius = center.distanceTo(start);
    const a1 = Math.atan2(start.y - center.y, start.x - center.x);
    const a2 = Math.atan2(end.y - center.y, end.x - center.x);
    let sweep = a2 - a1;
    if (sweep < 0) sweep += Math.PI * 2;
    return radius * sweep;
}
